// frontend/src/components/AvatarsManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  OpenInNew,
  Visibility,
  Edit,
  Delete,
  Add,
  Twitter,
  Telegram,
  Facebook,
  LinkedIn,
  Instagram,
  Reddit,
  Forum,
  PhotoCamera,
  WhatsApp,
  Close
} from '@mui/icons-material';

const AvatarsManagement = () => {
  const [avatars, setAvatars] = useState([]);
  const [filteredAvatars, setFilteredAvatars] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Platform-specific configuration templates
  const platformConfigs = {
    telegram: {
      name: 'Telegram',
      color: '#0088cc',
      icon: Telegram,
      requiredFields: ['api_id', 'api_hash', 'api_token', 'phone_number'],
      optionalFields: ['session_string', 'proxy_settings', 'flood_sleep_threshold'],
      fieldTypes: {
        api_id: 'number',
        api_hash: 'string',
        api_token: 'string',
        phone_number: 'string',
        session_string: 'string',
        proxy_settings: 'object',
        flood_sleep_threshold: 'number'
      },
      descriptions: {
        api_id: 'Telegram API ID from my.telegram.org',
        api_hash: 'Telegram API Hash from my.telegram.org',
        api_token: 'Bot token from @BotFather',
        phone_number: 'Phone number associated with account',
        session_string: 'Saved session string for authentication',
        proxy_settings: 'Proxy configuration for restricted regions',
        flood_sleep_threshold: 'Delay threshold to avoid flood limits'
      }
    },
    x: {
      name: 'X (Twitter)',
      color: '#000000',
      icon: Twitter,
      requiredFields: ['api_key', 'api_secret_key', 'access_token', 'access_token_secret'],
      optionalFields: ['bearer_token', 'webhook_url', 'rate_limit_buffer'],
      fieldTypes: {
        api_key: 'string',
        api_secret_key: 'string',
        access_token: 'string',
        access_token_secret: 'string',
        bearer_token: 'string',
        webhook_url: 'string',
        rate_limit_buffer: 'number'
      },
      descriptions: {
        api_key: 'Twitter API Key from developer portal',
        api_secret_key: 'Twitter API Secret Key',
        access_token: 'User Access Token',
        access_token_secret: 'User Access Token Secret',
        bearer_token: 'Bearer Token for app-only authentication',
        webhook_url: 'Webhook URL for real-time updates',
        rate_limit_buffer: 'Buffer time between API calls (seconds)'
      }
    },
    facebook: {
      name: 'Facebook',
      color: '#1877f2',
      icon: Facebook,
      requiredFields: ['app_id', 'app_secret', 'access_token'],
      optionalFields: ['page_access_token', 'webhook_verify_token', 'graph_api_version'],
      fieldTypes: {
        app_id: 'string',
        app_secret: 'string',
        access_token: 'string',
        page_access_token: 'string',
        webhook_verify_token: 'string',
        graph_api_version: 'string'
      },
      descriptions: {
        app_id: 'Facebook App ID from developers.facebook.com',
        app_secret: 'Facebook App Secret',
        access_token: 'User Access Token',
        page_access_token: 'Page Access Token for page management',
        webhook_verify_token: 'Webhook verification token',
        graph_api_version: 'Graph API version (e.g., v18.0)'
      }
    },
    instagram: {
      name: 'Instagram',
      color: '#E4405F',
      icon: Instagram,
      requiredFields: ['access_token', 'user_id'],
      optionalFields: ['business_account_id', 'webhook_secret'],
      fieldTypes: {
        access_token: 'string',
        user_id: 'string',
        business_account_id: 'string',
        webhook_secret: 'string'
      },
      descriptions: {
        access_token: 'Instagram Access Token',
        user_id: 'Instagram User ID',
        business_account_id: 'Business Account ID for Instagram Business',
        webhook_secret: 'Webhook secret for secure callbacks'
      }
    },
    linkedin: {
      name: 'LinkedIn',
      color: '#0077b5',
      icon: LinkedIn,
      requiredFields: ['client_id', 'client_secret', 'access_token'],
      optionalFields: ['organization_id', 'scope'],
      fieldTypes: {
        client_id: 'string',
        client_secret: 'string',
        access_token: 'string',
        organization_id: 'string',
        scope: 'string'
      },
      descriptions: {
        client_id: 'LinkedIn App Client ID',
        client_secret: 'LinkedIn App Client Secret',
        access_token: 'OAuth Access Token',
        organization_id: 'Organization ID for company pages',
        scope: 'OAuth scope permissions'
      }
    },
    reddit: {
      name: 'Reddit',
      color: '#FF4500',
      icon: Reddit,
      requiredFields: ['client_id', 'client_secret', 'user_agent'],
      optionalFields: ['username', 'password', 'refresh_token'],
      fieldTypes: {
        client_id: 'string',
        client_secret: 'string',
        user_agent: 'string',
        username: 'string',
        password: 'string',
        refresh_token: 'string'
      },
      descriptions: {
        client_id: 'Reddit App Client ID',
        client_secret: 'Reddit App Client Secret',
        user_agent: 'Unique user agent string',
        username: 'Reddit username',
        password: 'Reddit password',
        refresh_token: 'OAuth refresh token'
      }
    },
    whatsapp: {
      name: 'WhatsApp Business',
      color: '#25D366',
      icon: WhatsApp,
      requiredFields: ['access_token', 'phone_number_id'],
      optionalFields: ['business_account_id', 'webhook_verify_token'],
      fieldTypes: {
        access_token: 'string',
        phone_number_id: 'string',
        business_account_id: 'string',
        webhook_verify_token: 'string'
      },
      descriptions: {
        access_token: 'WhatsApp Business API Access Token',
        phone_number_id: 'Phone Number ID from WhatsApp Business',
        business_account_id: 'WhatsApp Business Account ID',
        webhook_verify_token: 'Webhook verification token'
      }
    },
    discord: {
      name: 'Discord',
      color: '#5865F2',
      icon: Forum,
      requiredFields: ['bot_token', 'application_id'],
      optionalFields: ['guild_id', 'public_key', 'permissions'],
      fieldTypes: {
        bot_token: 'string',
        application_id: 'string',
        guild_id: 'string',
        public_key: 'string',
        permissions: 'number'
      },
      descriptions: {
        bot_token: 'Discord Bot Token',
        application_id: 'Discord Application ID',
        guild_id: 'Discord Server (Guild) ID',
        public_key: 'Discord Application Public Key',
        permissions: 'Bot permissions integer'
      }
    },
    snapchat: {
      name: 'Snapchat',
      color: '#FFFC00',
      icon: PhotoCamera,
      requiredFields: ['client_id', 'client_secret'],
      optionalFields: ['redirect_uri', 'scope'],
      fieldTypes: {
        client_id: 'string',
        client_secret: 'string',
        redirect_uri: 'string',
        scope: 'string'
      },
      descriptions: {
        client_id: 'Snapchat App Client ID',
        client_secret: 'Snapchat App Client Secret',
        redirect_uri: 'OAuth redirect URI',
        scope: 'API access scope'
      }
    }
  };

  // Generate mock avatar data
  const generateMockAvatars = () => {
    const platforms = Object.keys(platformConfigs);

    const nicknames = [
      'mideast_observer', 'conflict_analyst', 'defense_reporter', 'geopolitical_expert',
      'security_watch', 'news_aggregator', 'field_journalist', 'policy_analyst',
      'regional_expert', 'military_analyst', 'peace_activist', 'humanitarian_watch',
      'un_reporter', 'diplomat_insider', 'intel_analyst', 'war_correspondent',
      'middle_east_today', 'breaking_alerts', 'crisis_monitor', 'strategic_analysis',
      'syria_updates', 'gaza_news', 'lebanon_live', 'iraq_watch', 'iran_insider',
      'turkey_times', 'gulf_observer', 'yemen_crisis', 'palestine_voice'
    ];

    const countries = [
      'United Arab Emirates', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain',
      'Oman', 'Jordan', 'Lebanon', 'Iraq', 'Iran', 'Turkey', 'Israel',
      'Palestine', 'Syria', 'Yemen', 'Egypt', 'Cyprus'
    ];

    const mockAvatars = [];
    let idCounter = 1;

    nicknames.forEach(nickname => {
      // Each nickname appears on 1-3 random platforms
      const numPlatforms = Math.floor(Math.random() * 2) + 1;
      const selectedPlatforms = platforms.sort(() => 0.5 - Math.random()).slice(0, numPlatforms);
      
      selectedPlatforms.forEach(platformKey => {
        const platform = platformConfigs[platformKey];
        const createdDays = Math.floor(Math.random() * 365) + 1;
        const lastActiveDays = Math.floor(Math.random() * 30);
        
        // Generate platform-specific configuration data
        const generatePlatformConfig = (platformKey) => {
          const config = platformConfigs[platformKey];
          const platformData = {};
          
          // Generate mock data for required fields
          config.requiredFields.forEach(field => {
            switch (config.fieldTypes[field]) {
              case 'number':
                if (field === 'api_id') platformData[field] = Math.floor(Math.random() * 9000000) + 1000000;
                else if (field === 'permissions') platformData[field] = Math.floor(Math.random() * 2048);
                else platformData[field] = Math.floor(Math.random() * 100) + 10;
                break;
              case 'string':
                if (field.includes('token') || field.includes('secret') || field.includes('key')) {
                  platformData[field] = generateRandomToken();
                } else if (field === 'phone_number') {
                  platformData[field] = `+971${Math.floor(Math.random() * 90000000) + 10000000}`;
                } else if (field === 'user_agent') {
                  platformData[field] = `${nickname}_bot/1.0`;
                } else if (field.includes('id')) {
                  platformData[field] = `${platformKey}_${Math.floor(Math.random() * 1000000)}`;
                } else {
                  platformData[field] = `${field}_${Math.random().toString(36).substring(7)}`;
                }
                break;
              case 'object':
                if (field === 'proxy_settings') {
                  platformData[field] = {
                    enabled: Math.random() > 0.7,
                    host: '192.168.1.1',
                    port: 8080,
                    type: 'SOCKS5'
                  };
                }
                break;
              default:
                platformData[field] = null;
                break;
            }
          });
          
          // Randomly add some optional fields
          config.optionalFields.forEach(field => {
            if (Math.random() > 0.6) { // 40% chance to include optional field
              switch (config.fieldTypes[field]) {
                case 'number':
                  platformData[field] = Math.floor(Math.random() * 100) + 1;
                  break;
                case 'string':
                  if (field.includes('token') || field.includes('secret')) {
                    platformData[field] = generateRandomToken();
                  } else if (field.includes('url')) {
                    platformData[field] = `https://webhook.${platformKey}.com/callback`;
                  } else if (field === 'scope') {
                    platformData[field] = 'read,write,admin';
                  } else {
                    platformData[field] = `${field}_${Math.random().toString(36).substring(7)}`;
                  }
                  break;
                default:
                  platformData[field] = null;
                  break;
              }
            }
          });
          
          return platformData;
        };
        
        const generateRandomToken = () => {
          return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        };
        
        const platformConfig = generatePlatformConfig(platformKey);
        
        mockAvatars.push({
          id: idCounter++,
          nickname: nickname,
          platform: platformKey,
          platformName: platform.name,
          platformIcon: platform.icon,
          platformColor: platform.color,
          platformId: `${platformKey}_${Math.floor(Math.random() * 1000000)}`,
          profileUrl: `https://${platformKey}.com/${nickname}`,
          avatarUrl: `https://ui-avatars.com/api/?name=${nickname}&background=${platform.color.replace('#', '')}&color=fff&size=40`,
          followers: Math.floor(Math.random() * 100000) + 500,
          following: Math.floor(Math.random() * 5000) + 100,
          posts: Math.floor(Math.random() * 10000) + 50,
          verified: Math.random() > 0.7,
          country: countries[Math.floor(Math.random() * countries.length)],
          language: Math.random() > 0.5 ? 'Arabic' : 'English',
          createdAt: new Date(Date.now() - createdDays * 24 * 60 * 60 * 1000),
          lastActive: new Date(Date.now() - lastActiveDays * 24 * 60 * 60 * 1000),
          status: Math.random() > 0.1 ? 'active' : 'inactive',
          description: `${nickname.replace('_', ' ')} - Middle East analyst and reporter focusing on regional developments`,
          tags: ['News', 'Politics', 'Middle East', 'Analysis'].slice(0, Math.floor(Math.random() * 4) + 1),
          platformConfig: platformConfig,
          configStatus: Object.keys(platformConfig).length >= platformConfigs[platformKey].requiredFields.length ? 'configured' : 'incomplete'
        });
      });
    });

    return mockAvatars.sort((a, b) => b.followers - a.followers);
  };

  useEffect(() => {
    const mockData = generateMockAvatars();
    setAvatars(mockData);
    setFilteredAvatars(mockData);
  }, []);

  // Filter avatars based on search and filters
  useEffect(() => {
    let filtered = [...avatars];

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(avatar =>
        avatar.nickname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        avatar.platform.toLowerCase().includes(searchQuery.toLowerCase()) ||
        avatar.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        avatar.platformId.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Platform filter
    if (platformFilter) {
      filtered = filtered.filter(avatar => avatar.platform === platformFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(avatar => avatar.status === statusFilter);
    }

    // Country filter
    if (countryFilter) {
      filtered = filtered.filter(avatar => avatar.country === countryFilter);
    }

    setFilteredAvatars(filtered);
  }, [searchQuery, platformFilter, statusFilter, countryFilter, avatars]);

  // Get unique values for filters
  const uniquePlatforms = [...new Set(avatars.map(avatar => avatar.platform))].sort();
  const uniqueStatuses = [...new Set(avatars.map(avatar => avatar.status))].sort();
  const uniqueCountries = [...new Set(avatars.map(avatar => avatar.country))].sort();

  const clearFilters = () => {
    setSearchQuery('');
    setPlatformFilter('');
    setStatusFilter('');
    setCountryFilter('');
  };

  const handleViewDetails = (avatar) => {
    setSelectedAvatar(avatar);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAvatar(null);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const getPlatformIcon = (IconComponent, color) => {
    return <IconComponent sx={{ fontSize: 20, color }} />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  // DataGrid columns configuration
  const columns = [
    {
      field: 'avatarUrl',
      headerName: 'Avatar',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt={params.row.nickname}
          sx={{ width: 40, height: 40 }}
        />
      )
    },
    {
      field: 'nickname',
      headerName: 'Nickname',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
          {params.row.verified && (
            <Typography sx={{ color: '#1DA1F2', fontSize: '16px' }}>
              ✓
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getPlatformIcon(params.row.platformIcon, params.row.platformColor)}
          <Box>
            <Typography variant="body2">
              {params.row.platformName}
            </Typography>
            <Chip
              label={params.row.configStatus}
              size="small"
              color={params.row.configStatus === 'configured' ? 'success' : 'warning'}
              sx={{ height: 16, fontSize: '0.6rem' }}
            />
          </Box>
        </Box>
      )
    },
    {
      field: 'platformId',
      headerName: 'Platform ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'followers',
      headerName: 'Followers',
      width: 100,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2">
          {formatNumber(params.value)}
        </Typography>
      )
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 150
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDate(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(params.row)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open Profile">
            <IconButton
              size="small"
              component={Link}
              href={params.row.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" disabled>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" disabled color="error">
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Summary stats
  const totalAvatars = filteredAvatars.length;
  const platformCounts = filteredAvatars.reduce((acc, avatar) => {
    acc[avatar.platform] = (acc[avatar.platform] || 0) + 1;
    return acc;
  }, {});
  const activeCount = filteredAvatars.filter(a => a.status === 'active').length;
  const verifiedCount = filteredAvatars.filter(a => a.verified).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Avatars Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage social media profiles and avatars across all platforms
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Avatars
              </Typography>
              <Typography variant="h4" color="primary">
                {totalAvatars}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Profiles
              </Typography>
              <Typography variant="h4" color="success.main">
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Verified Accounts
              </Typography>
              <Typography variant="h4" color="info.main">
                {verifiedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Platforms
              </Typography>
              <Typography variant="h4">
                {Object.keys(platformCounts).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Search by nickname, platform, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="medium">
            <InputLabel>Platform</InputLabel>
            <Select
              value={platformFilter}
              label="Platform"
              onChange={(e) => setPlatformFilter(e.target.value)}
            >
              <MenuItem value="">All Platforms</MenuItem>
              {uniquePlatforms.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="medium">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {uniqueStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="medium">
            <InputLabel>Country</InputLabel>
            <Select
              value={countryFilter}
              label="Country"
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <MenuItem value="">All Countries</MenuItem>
              {uniqueCountries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={clearFilters}
            disabled={!searchQuery && !platformFilter && !statusFilter && !countryFilter}
            sx={{ height: '56px' }}
          >
            Clear
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={1}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            disabled
            sx={{ height: '56px' }}
          >
            Add
          </Button>
        </Grid>
      </Grid>

      {/* Avatars DataGrid */}
      <Paper sx={{ height: 650, width: '100%' }}>
        <DataGrid
          rows={filteredAvatars}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableSelectionOnClick
          autoHeight={false}
          getRowId={(row) => `${row.platform}-${row.id}`}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Paper>

      {/* Avatar Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown') {
            handleCloseDialog();
          }
        }}
      >
        {selectedAvatar && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={selectedAvatar.avatarUrl}
                    alt={selectedAvatar.nickname}
                    sx={{ width: 50, height: 50 }}
                  />
                  <Box>
                    <Typography variant="h6">
                      {selectedAvatar.nickname}
                      {selectedAvatar.verified && (
                        <Typography component="span" sx={{ color: '#1DA1F2', ml: 1 }}>
                          ✓
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getPlatformIcon(selectedAvatar.platformIcon, selectedAvatar.platformColor)}
                      <Typography variant="body2" color="textSecondary">
                        {selectedAvatar.platform.charAt(0).toUpperCase() + selectedAvatar.platform.slice(1)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDialog}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Profile Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Platform ID</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedAvatar.platformId}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Profile URL</Typography>
                    <Link href={selectedAvatar.profileUrl} target="_blank" rel="noopener noreferrer">
                      {selectedAvatar.profileUrl}
                    </Link>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Description</Typography>
                    <Typography variant="body1">{selectedAvatar.description}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Tags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {selectedAvatar.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Analytics</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Followers</Typography>
                    <Typography variant="h5" color="primary.main">
                      {formatNumber(selectedAvatar.followers)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Following</Typography>
                    <Typography variant="body1">{formatNumber(selectedAvatar.following)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Posts</Typography>
                    <Typography variant="body1">{formatNumber(selectedAvatar.posts)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Country</Typography>
                    <Typography variant="body1">{selectedAvatar.country}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Language</Typography>
                    <Typography variant="body1">{selectedAvatar.language}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Created</Typography>
                    <Typography variant="body1">{formatDate(selectedAvatar.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Last Active</Typography>
                    <Typography variant="body1">{formatDate(selectedAvatar.lastActive)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip
                      label={selectedAvatar.status}
                      color={getStatusColor(selectedAvatar.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button variant="outlined" disabled>Edit Profile</Button>
              <Button
                variant="contained"
                startIcon={<OpenInNew />}
                href={selectedAvatar.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                component={Link}
              >
                View Profile
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* No results message */}
      {filteredAvatars.length === 0 && searchQuery && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No avatars found matching your search criteria.
        </Alert>
      )}
    </Box>
  );
};

export default AvatarsManagement;